import invariant from 'react-utils/invariant';
import CartographerReporter, { CartographerEndpoint } from './CartographerReporter';
import { onVisibilityChange, visibilityState, wasHiddenOnScriptStart } from '../visibility';
import { PageLoadMetrics } from '../Metrics';
import { isReload, performanceGetEntriesByType } from '../navigation';
import { getHubHttpData } from '../httpRequestsStats';
import { getIsAiCopilotEnabled } from '../aiCopilot';
import Raven from 'raven-js';
import { cleanseRoute } from '../cleanseRoute';
import { isPrerendering } from '../env';
const STATIC_DOMAIN_REGEX = /https:\/\/(static|local).hsappstatic.net\//;

// If the duration of the request is <= 10ms mark the request as a cache hit.
const CACHE_DURATION_MS = 10;
export default class ReaganCompatReporter extends CartographerReporter {
  constructor(options) {
    super(options);
    this.abandonedTimes = [];
    this.lastAbandonedTimestamp = null;
    this.finished = false;
    this.wasHidden = visibilityState() === 'hidden';
    this.prerendering = isPrerendering();
    // These flags measure if the page was abandoned within 500ms for another tab or window, and
    // remained invisible. This is used to determine if the page was abandoned before the user was
    // fruterated, we are considering filtering these out becasue they tab becomes throttled and
    // loads very slowly.
    this.hiddenEntirelyBeforeRhumbFinished = wasHiddenOnScriptStart && visibilityState() === 'hidden';
    this.hiddenWithin1000ms = visibilityState() === 'hidden';
    this.hiddenWithin500ms = visibilityState() === 'hidden';
    this.setCustomAttribute('currentVisibility', visibilityState());
    this.setCustomAttribute('visibility', visibilityState());
    let becameVisibleAgain = false;
    onVisibilityChange(state => {
      this.setCustomAttribute('currentVisibility', state);
      if (this.hiddenEntirelyBeforeRhumbFinished && state === 'visible') {
        this.hiddenEntirelyBeforeRhumbFinished = false;
      }
      if (this.wasHidden && state === 'visible') {
        becameVisibleAgain = true;
        this.hiddenWithin1000ms = false;
        this.hiddenWithin500ms = false;
      }
      if (state === 'hidden' && !this.wasHidden) {
        this.wasHidden = true;
        this.setCustomAttribute('visibility', 'hidden');
        if (!becameVisibleAgain) {
          const now = performance.now();
          if (now <= 1000) {
            this.hiddenWithin1000ms = true;
          }
          if (now <= 500) {
            this.hiddenWithin500ms = true;
          }
        }
      }
    });
  }
  getCacheStatusData() {
    const cacheStatusData = {};
    this.performanceEntries().forEach(timing => {
      if (timing.name.endsWith('.js')) {
        const fileName = timing.name.replace(STATIC_DOMAIN_REGEX, '');
        cacheStatusData[fileName] = {
          cached: timing.duration <= CACHE_DURATION_MS,
          duration: timing.duration
        };
      }
    });
    return cacheStatusData;
  }
  getNumFailedImages() {
    return Array.from(document.getElementsByTagName('img')).reduce((total, ele) => {
      return ele.src && ele.naturalHeight === 0 && ele.naturalWidth === 0 ? total + 1 : total;
    }, 0);
  }
  finish(attrs, checks) {
    var _performanceGetEntrie;
    const hubHttpData = getHubHttpData(attrs.timestamp);
    const avgDurationBeforePreviousRhumbAborts = this.abandonedTimes.reduce((acc, duration) => {
      return acc + duration / this.abandonedTimes.length;
    }, 0);
    const reaganTiming = {};
    Object.keys(checks).forEach(marker => {
      if (checks[marker]) {
        reaganTiming[`marker_timing_${marker}`] = checks[marker].timestamp;
      }
    });
    this.setCustomAttribute('numReaganChecksStarted', this.abandonedTimes.length + 1);
    this.setCustomAttribute('numPreviousReaganChecksAborted', this.abandonedTimes.length);
    this.setCustomAttribute('avgDurationBeforePreviousReaganAborts', avgDurationBeforePreviousRhumbAborts);
    this.setCustomAttribute('numPreviousReaganChecksFailed', 0);
    this.setCustomAttribute('numPreviousReaganChecksSuccessful', 0);
    const activationStartMs =
    // @ts-expect-error PerformanceEntry.activationStart is only available in Chromium
    ((_performanceGetEntrie = performanceGetEntriesByType('navigation')) === null || _performanceGetEntrie === void 0 || (_performanceGetEntrie = _performanceGetEntrie[0]) === null || _performanceGetEntrie === void 0 ? void 0 : _performanceGetEntrie.activationStart) || 0;
    PageLoadMetrics.counter('rhumb-finished', {
      status: attrs.status
    }).increment();
    Raven.capturePageEvent('rhumbFinished', {
      extra: Object.assign({}, attrs, {
        duration: attrs.duration / 1000
      }, hubHttpData, {
        numChecksStarted: this.abandonedTimes.length + 1,
        numPreviousChecksAborted: this.abandonedTimes.length,
        avgDurationBeforePreviousRhumbAborts,
        numFailedImages: this.getNumFailedImages(),
        allVisibleMarkers: JSON.stringify(Object.keys(checks)),
        wasPrerendered: this.prerendering || activationStartMs > 0,
        wasHiddenOnScriptStart,
        wasEverHidden: this.wasHidden,
        wasHiddenWhenRhumbFinished: visibilityState() === 'hidden',
        hiddenEntirelyBeforeRhumbFinished: this.hiddenEntirelyBeforeRhumbFinished,
        hiddenWithin1000ms: this.hiddenWithin1000ms,
        hiddenWithin500ms: this.hiddenWithin500ms
      }, reaganTiming)
    });

    // send to Cartographer
    this.sendActions([{
      to: {
        pathname: attrs.pathname,
        route: attrs.route,
        scenario: attrs.scenario
      },
      status: attrs.status,
      wasHidden: this.wasHidden,
      isHidden: visibilityState() === 'hidden',
      duration: attrs.duration / 1000,
      // convert to seconds
      failureType: attrs.failureType,
      isReload: isReload(),
      timestamp: attrs.timestamp
    }], CartographerEndpoint.Navigation);
  }
  report(action) {
    if (action.type === 'COMPONENT_RENDERED' || !this || this.finished) {
      return;
    }
    const {
      entry: {
        timestamp: baseTimestamp,
        checks,
        expiredTimestamp,
        pathname
      },
      routeSpec
    } = action.payload;
    switch (action.type) {
      case 'ROUTE_SUCCEEDED':
      case 'ROUTE_PARTIAL_SUCCESS':
      case 'ROUTE_FAILED':
      case 'ROUTE_TIMEOUT_EXPIRED':
      case 'ROUTE_UNEXPECTED':
        {
          this.finished = true;
          break;
        }
      default:
    }
    const isAiCopilotEnabled = String(getIsAiCopilotEnabled());
    switch (action.type) {
      case 'ROUTE_STARTED':
        {
          if (this.lastAbandonedTimestamp) {
            this.abandonedTimes.push(baseTimestamp - this.lastAbandonedTimestamp);
          }
          break;
        }
      case 'ROUTE_ABANDONED':
        {
          this.lastAbandonedTimestamp = baseTimestamp;
          break;
        }
      case 'ROUTE_PARTIAL_SUCCESS':
        {
          const {
            partialSuccess,
            route
          } = routeSpec;
          const {
            extra: {
              scenario
            }
          } = action.payload;
          const markers = partialSuccess[scenario] || [];
          const {
            duration,
            timestamp,
            selector
          } = this.toTimings(checks, baseTimestamp, markers);
          if (!this.wasHidden) {
            PageLoadMetrics.timer('partial_success', {
              scenario,
              route: cleanseRoute(route),
              isAiCopilotEnabled
            }).update(baseTimestamp + duration);
          }
          this.finish({
            status: 'partial_success',
            duration,
            timestamp,
            pathname,
            scenario,
            selector,
            route
          }, checks);
          this.performanceMark(`mark_all_success`);
          break;
        }
      case 'ROUTE_SUCCEEDED':
        {
          var _performanceGetEntrie2;
          const {
            success,
            route
          } = routeSpec;
          const {
            extra: {
              scenario
            }
          } = action.payload;
          if (process.env.NODE_ENV !== 'production') {
            invariant(success[scenario].length > 0, 'routeSpec for %s must have at least one `success` marker for %s', route, scenario);
          }
          const markers = success[scenario] || [];
          const {
            duration,
            timestamp,
            selector
          } = this.toTimings(checks, baseTimestamp, markers);
          if (!this.wasHidden) {
            PageLoadMetrics.timer('succeeded', {
              scenario,
              route: cleanseRoute(route),
              isAiCopilotEnabled
            }).update(baseTimestamp + duration);
          }
          const activationStartMs =
          // @ts-expect-error PerformanceEntry.activationStart is only available in Chromium
          ((_performanceGetEntrie2 = performanceGetEntriesByType('navigation')) === null || _performanceGetEntrie2 === void 0 || (_performanceGetEntrie2 = _performanceGetEntrie2[0]) === null || _performanceGetEntrie2 === void 0 ? void 0 : _performanceGetEntrie2.activationStart) || 0;
          if (activationStartMs > 0) {
            PageLoadMetrics.timer('succeeded-prerender', {
              scenario,
              route: cleanseRoute(route),
              isAiCopilotEnabled
            }).update(baseTimestamp + duration - activationStartMs);
          }
          this.finish({
            status: 'success',
            duration,
            timestamp,
            pathname,
            scenario,
            selector,
            route
          }, checks);
          this.performanceMark(`mark_all_success`);
          break;
        }
      case 'ROUTE_FAILED':
        {
          const {
            route,
            error
          } = routeSpec;
          const markers = error.filter(marker => checks[marker]);
          if (process.env.NODE_ENV !== 'production') {
            invariant(markers.length > 0, 'routeSpec for %s must have at least one `failure` marker for', route);
          }
          const {
            duration,
            timestamp,
            selector
          } = this.toTimings(checks, baseTimestamp, markers);
          const failedMarker = markers.length ? markers[0] : '';
          if (!this.wasHidden) {
            PageLoadMetrics.counter('failed', {
              route: cleanseRoute(route),
              selector: failedMarker,
              isAiCopilotEnabled
            }).increment();
          }
          this.finish({
            status: 'failure',
            failureType: 'errorSelector',
            selector,
            duration,
            timestamp,
            route,
            pathname
          }, checks);
          this.performanceMark(`mark_all_failure`);
          break;
        }
      case 'ROUTE_TIMEOUT_EXPIRED':
        {
          const {
            route
          } = routeSpec;
          if (!this.wasHidden) {
            PageLoadMetrics.counter('timeouts', {
              route: cleanseRoute(route),
              isAiCopilotEnabled
            }).increment();
          }
          if (!this.hiddenWithin1000ms) {
            PageLoadMetrics.counter('timeouts_1000ms_visiblity_threshold', {
              route: cleanseRoute(route),
              isAiCopilotEnabled
            }).increment();
          }
          if (!this.hiddenWithin500ms) {
            PageLoadMetrics.counter('timeouts_500ms_visiblity_threshold', {
              route: cleanseRoute(route),
              isAiCopilotEnabled
            }).increment();
          }
          if (!this.hiddenEntirelyBeforeRhumbFinished) {
            PageLoadMetrics.counter('timeouts_entirely_hidden_filtered_out', {
              route: cleanseRoute(route),
              isAiCopilotEnabled
            }).increment();
          }
          let timeoutDuration = expiredTimestamp || 0;
          if (!isNaN(baseTimestamp)) {
            timeoutDuration = timeoutDuration - baseTimestamp;
          }
          this.finish({
            status: 'failure',
            failureType: 'watchdogExpired',
            duration: timeoutDuration,
            timestamp: expiredTimestamp || performance.now(),
            route
          }, checks);
          this.performanceMark(`mark_all_failure_watchdog_expired`);
          break;
        }
      default:
    }
  }
}